/**
 * Auto-inject the business's Meta Pixel — the id is auto-discovered server-side and sent on the
 * salon payload (`salon.metaPixelId`), so a business never pastes anything. The Pixel adds
 * site-visitor + viewed-product retargeting audiences and browser-side conversion measurement
 * (server-side conversion upload already covers bidding — this is the belt-and-suspenders layer).
 * Fully best-effort; a missing/blocked pixel never breaks the store.
 *
 * ONE SHOP'S EVENTS GO TO ONE SHOP'S PIXEL.
 *
 * Every business on stylon.online shares the page, and every pixel started in the tab lives in
 * the one `fbq`. Three leaks followed from that:
 *
 *   · `fbq('track')` sends to every pixel in the tab, so a shopper who opened one business and
 *     then another sent the second one's product views and purchases to the first one's pixel.
 *     Events are sent with `trackSingle`.
 *   · Meta's script sends a PageView of its own to every pixel in the tab each time the address
 *     changes (pushState, replaceState, back), so after /explore → shop A → shop B every page of
 *     B reached A's pixel with B's address. Each pixel is marked `trackSingleOnly` as it starts,
 *     which stops that, and a page's PageView is sent from here instead: once per page, to the
 *     pixel of the shop the page belongs to (`trackPageChange`).
 *   · "The page's pixel" was whichever pixel the tab had loaded last, and the "Recently ordered"
 *     strip on /home adds any business's product. Every event names the shop it belongs to, and
 *     one that is not the shop the page is on goes nowhere.
 *
 * An event fired before the page's shop has loaded waits for it. A shopper who lands on a
 * product page from a catalogue ad gets the product before the (heavier) business payload, so
 * its ViewContent used to fire with no pixel on the page and was lost. What waits is judged when
 * the shop's pixel is known, and forgotten when the page moves on to another shop.
 */
import { v4 as uuidv4 } from 'uuid';
import { isDomainMode } from './domainMode';
import { relayShopperStep } from './shopperSteps';
import { afterPageLoad } from './afterPageLoad';

// The shop's currency, remembered at init: a cart door's snapshot carries a price but not
// what it is priced in, and Meta ignores a value that has no currency.
let shopCurrency = 'ILS';
/**
 * The names the shop the page is loading or showing goes by: the key it was loaded with (its
 * slug, or the host on its own domain) and the slug its payload carries. Empty before any shop.
 */
let shopNames = new Set();
/** That shop's pixel: undefined until the shop has loaded, null when it has none. */
let shopPixel;
/** Pixels already started in this tab — `init` happens once each. */
const started = new Set();
/** Events from before the shop's pixel was known, each with the shop it names. */
let waiting = [];
const WAITING_MAX = 50;
/** The address the page is at, and whether its PageView has yet to be sent. */
let pageAddress = currentAddress();
let pageViewOwed = true;
/**
 * Whether the page's shop hands its shoppers' steps to the server as well (its payload's
 * `metaServerEvents`), and the slug that door is addressed by.
 */
let shopRelays = false;
let shopSlug = null;

/**
 * A page began loading a shop. If it is not the shop already known, its events wait until
 * that shop's pixel is — rather than going to the pixel of the shop before it — and what was
 * waiting for another shop is let go.
 */
export function expectShop(key) {
  if (!key || shopNames.has(String(key))) return;
  shopNames = new Set([String(key)]);
  shopPixel = undefined;
  shopRelays = false;
  shopSlug = null;
  waiting = waiting.filter((held) => held.shop === String(key));
}

export function initMetaPixel(pixelId, { currency, shop, slug, serverEvents } = {}) {
  if (typeof window === 'undefined') return;
  if (shop) {
    // A late answer for a shop the shopper has already left says nothing about this page.
    if (shopNames.size && !shopNames.has(String(shop))) return;
    shopNames.add(String(shop));
  }
  // On its own domain a shop is loaded by host, and its events name its slug.
  if (slug) shopNames.add(String(slug));
  if (currency) shopCurrency = String(currency).toUpperCase();
  shopPixel = pixelId ? String(pixelId) : null;
  // The server's door is addressed by the shop's slug: on its own domain the shop was loaded by host.
  if (slug || shop) shopSlug = String(slug || shop);
  shopRelays = serverEvents === true;
  if (shopPixel && !started.has(shopPixel)) {
    try {
      /* eslint-disable */
      !(function (f, b, e, v, n) {
        if (f.fbq) return;
        n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
        if (!f._fbq) f._fbq = n;
        n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
        // Meta's snippet, with the script itself held until the page has loaded (utils/afterPageLoad):
        // `fbq` is a queue until then, and every init, flag and event below waits in it, in order,
        // for the script to arrive — so the PageView is the page's first event, as before.
        afterPageLoad(function () {
          var t = b.createElement(e); t.async = !0; t.src = v;
          var s = b.getElementsByTagName(e)[0];
          if (s && s.parentNode) s.parentNode.insertBefore(t, s); else (b.head || b.documentElement).appendChild(t);
        });
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      /* eslint-enable */
      window.fbq('init', shopPixel);
      // Only after `init`: Meta ignores the flag for a pixel it does not hold yet.
      window.fbq('set', 'trackSingleOnly', true, shopPixel);
      started.add(shopPixel);
    } catch (_) {
      /* never throw from instrumentation */
    }
  }
  sendPageView();
  const held = waiting;
  waiting = [];
  held.forEach((event) => send(event.shop, event.build, event.step));
}

/**
 * The page changed without a page load — the storefront is one app. Its PageView is owed to
 * the shop the new page belongs to: sent now when that shop's pixel is already known, or when
 * its pixel is. An address that did not change is not a new page (Meta's own rule).
 */
export function trackPageChange() {
  if (typeof window === 'undefined') return;
  const address = currentAddress();
  if (address === pageAddress) return;
  pageAddress = address;
  pageViewOwed = true;
  sendPageView();
}

/** The browser brought the page back from its back-forward cache: Meta counts that as a view. */
export function trackPageRestored() {
  if (typeof window === 'undefined') return;
  pageViewOwed = true;
  sendPageView();
}

function sendPageView() {
  if (!pageViewOwed || !shopPixel || !window.fbq) return;
  try {
    if (!pageIsTheShops()) return;
    pageViewOwed = false;
    window.fbq('trackSingle', shopPixel, 'PageView');
  } catch (_) {
    /* never throw from instrumentation */
  }
}

/**
 * Whether the page the tab is on belongs to the page's shop. Every page of a business's own
 * domain does. On stylon.online a shop's pages live under its slug (/dk-store,
 * /dk-store/p/…); /home, /explore and /profile belong to no shop.
 */
function pageIsTheShops() {
  if (isDomainMode()) return true;
  const first = String(window.location?.pathname || '').split('/')[1] || '';
  let segment = first;
  try {
    segment = decodeURIComponent(first);
  } catch (_) {
    /* a malformed address is compared as it is */
  }
  return !!segment && shopNames.has(segment);
}

function currentAddress() {
  if (typeof window === 'undefined') return '';
  const { pathname = '', search = '', hash = '' } = window.location || {};
  return `${pathname}${search}${hash}`;
}

/**
 * Send one event to the pixel of the shop it names — or hold it until the pixel of the shop
 * the page is loading is known. An event that names no shop, or not the page's, goes nowhere.
 *
 * A shopper's `step` (a product view, an add to cart, a checkout begun) is also handed to the
 * server when the shop hands its steps over — whether or not the pixel took it: a blocked or broken
 * pixel is exactly the step the server's copy is for.
 */
function send(shop, build, step) {
  if (typeof window === 'undefined' || !shop) return;
  const of = String(shop);
  if (shopPixel === undefined) {
    // Judged when the pixel is known: only then are all the shop's names (its slug, on its own
    // domain) known too.
    if (shopNames.size && waiting.length < WAITING_MAX) waiting.push({ shop: of, build, step });
    return;
  }
  if (!shopNames.has(of) || !shopPixel) return;
  let built;
  try {
    built = build();
  } catch (_) {
    return; /* never throw from instrumentation */
  }
  const [event, params, options] = built;
  if (window.fbq) {
    try {
      if (options) window.fbq('trackSingle', shopPixel, event, params, options);
      else window.fbq('trackSingle', shopPixel, event, params);
    } catch (_) {
      /* never throw from instrumentation */
    }
  }
  if (step && shopRelays && shopSlug) relayShopperStep(shopSlug, stepFor(event, params, options));
}

/** A step's event id: its kind and a new uuid, minted when the step happens, for the pixel and the server alike. */
function stepId(kind) {
  return `${kind}-${uuidv4()}`;
}

/** The step the server is handed: the pixel's event, under the pixel's own id, naming what the pixel named. */
function stepFor(event, params, options) {
  const contents = Array.isArray(params.contents)
    ? params.contents
    : (params.content_ids || []).map((id) => ({ id, quantity: 1 }));
  return {
    eventName: event,
    eventId: options.eventID,
    eventSourceUrl: currentPage(),
    contents,
    value: Math.round((Number(params.value) || 0) * 100) / 100,
    currency: params.currency,
  };
}

function currentPage() {
  const { href, origin = '', pathname = '', search = '', hash = '' } = window.location || {};
  return typeof href === 'string' && href ? href : `${origin}${pathname}${search}${hash}`;
}

/**
 * Fire a conversion event (Purchase for an order, Lead for a booking). Best-effort.
 *
 * Both carry the event id the server sends its own copy under — `order-<id>` for a Purchase,
 * `booking-<id>` for a Lead — so Meta merges the two instead of counting one sale or one
 * booking twice.
 *
 * A Purchase names what was bought, in the catalogue feed's ids: `items` are the order's lines
 * (`productVariantId`) or the cart's (`variantId`), with their quantities — the `contents` the
 * server's Purchase carries. Only paid orders are Purchases; see utils/webConversion.
 */
export function trackPixelConversion(kind, { value, currency, id, shop, items } = {}) {
  send(shop, () => {
    const params = { value: Number(value) || 0, currency: currency || shopCurrency };
    if (kind !== 'ORDER') return id ? ['Lead', params, { eventID: `booking-${id}` }] : ['Lead', params];
    const contents = (Array.isArray(items) ? items : [])
      .map((line) => ({
        id: line?.productVariantId ?? line?.variantId,
        quantity: Math.max(1, Math.floor(Number(line?.quantity) || 1)),
      }))
      .filter((line) => line.id)
      .map((line) => ({ ...line, id: String(line.id) }));
    if (contents.length) {
      params.content_type = 'product';
      params.content_ids = contents.map((line) => line.id);
      params.contents = contents;
    }
    return id ? ['Purchase', params, { eventID: `order-${id}` }] : ['Purchase', params];
  });
}

/*
 * PRODUCT EVENTS, IN THE CATALOGUE FEED'S IDS.
 *
 * A catalogue ad learns what to show each person from what they viewed, added and bought,
 * matched by id against the feed (GET /public/salons/:slug/meta-catalog.csv). There, a
 * VARIANT is an item and a PRODUCT is the item group holding its variants — so a product
 * page speaks of the group, and a cart line of the exact variant.
 *
 * EACH ONE HAS ONE EVENT ID, SHARED WITH THE SERVER. A product view, an add to cart and a
 * checkout begun are minted an id when they happen — `vc-`, `atc-` or `ic-` and a uuid, never a
 * Purchase's `order-` or a Lead's `booking-` — which goes with the pixel's event and with the step
 * handed to the server (utils/shopperSteps.js). The server's copy to Meta carries the same id, and
 * Meta merges the two. One call is one step: the product page tells of a product once per page
 * view, the cart of each add once, the checkout once per visit.
 */

/** A product page of `shop` was seen. */
export function trackViewContent({ productId, value, shop } = {}) {
  if (!productId) return;
  const eventID = stepId('vc');
  send(shop, () => ['ViewContent', {
    content_type: 'product_group',
    content_ids: [String(productId)],
    // `null` = the page shows no price (an ask-us product): say nothing rather than a value of 0.
    ...(value === null ? {} : { value: Number(value) || 0, currency: shopCurrency }),
  }, { eventID }], true);
}

/**
 * A checkout of `shop` opened, holding `items` — the cart's lines ({ variantId, quantity,
 * snapshot: { price } }), each named by its variant, as its AddToCart was.
 *
 * `value` is what the shopper is charged, as the checkout's quote prices it — the Purchase's own
 * basis (see Pages/Checkout/useInitiateCheckout). Zero is a value: a basket its discount pays for
 * is charged nothing. Only when the checkout has no quote to say is the basket valued at the
 * price the cart holds each line at.
 */
export function trackInitiateCheckout({ items, shop, value } = {}) {
  const lines = (Array.isArray(items) ? items : [])
    .filter((line) => line?.variantId != null && line.variantId !== '')
    .map((line) => ({
      id: String(line.variantId),
      quantity: Math.max(1, Math.floor(Number(line.quantity) || 1)),
      price: Number(line.snapshot?.price) || 0,
    }));
  if (!lines.length) return;
  const charged = typeof value === 'number' && Number.isFinite(value) ? value : null;
  const atListPrice = lines.reduce((sum, line) => sum + line.price * line.quantity, 0);
  const eventID = stepId('ic');
  send(shop, () => ['InitiateCheckout', {
    content_type: 'product',
    content_ids: lines.map((line) => line.id),
    contents: lines.map(({ id, quantity }) => ({ id, quantity })),
    num_items: lines.reduce((n, line) => n + line.quantity, 0),
    value: Math.round((charged ?? atListPrice) * 100) / 100,
    currency: shopCurrency,
  }, { eventID }], true);
}

/** A variant went into `shop`'s cart. */
export function trackAddToCart({ variantId, quantity, unitPrice, shop } = {}) {
  if (!variantId) return;
  const qty = Math.max(1, Math.floor(Number(quantity) || 1));
  const eventID = stepId('atc');
  send(shop, () => ['AddToCart', {
    content_type: 'product',
    content_ids: [String(variantId)],
    contents: [{ id: String(variantId), quantity: qty }],
    value: Math.round((Number(unitPrice) || 0) * qty * 100) / 100,
    currency: shopCurrency,
  }, { eventID }], true);
}
